// react
import { FC, useMemo } from 'react'
// i18n
import { useTranslation } from '@/hooks'
// components
import CopyToClipboard from 'react-copy-to-clipboard'
import { QRCodeSVG as QRCodeReact } from 'qrcode.react'
import { Tooltip } from '@/components/Tooltip'
// assets
import MPCDepositWarnIcon from '@/icons/mpc-deposit-warn-icon.png'
// utils
import { shouldShowLargeDepositWarning } from '../../utils'
// types
import { MPCDepositProps } from './types'

const MPC: FC<MPCDepositProps> = ({
  currentActiveChain,
  selectedToken,
  address,
  onDeposit,
  onCopy,
  onClose,
}) => {
  const { t } = useTranslation()

  // 判断是否需要显示 25 万超出额度限制的提示
  // 使用工具函数，支持 chainId 和 chain 名称两种判断方式
  const showLargeDepositWarning = useMemo(() => {
    return shouldShowLargeDepositWarning(currentActiveChain || {})
  }, [currentActiveChain])

  return (
    <>
      {/* QR Code Section */}
      <div className="flex justify-center mt-4 mb-3">
        {address && (
          <div className="bg-white rounded-lg">
            <QRCodeReact
              value={address}
              size={120}
              level="L"
              bgColor="#ffffff"
              fgColor="#000000"
              marginSize={0}
            />
          </div>
        )}
      </div>

      {/* Address Section */}
      {address && (
        <div className="flex items-center justify-between bg-fill-input-primary rounded-lg py-2 px-3 mb-3">
          <span className="flex-1 text-text-primary text-sm font-medium break-all mr-3">
            {address}
          </span>
          <CopyToClipboard
            key="copy"
            onCopy={() => {
              // 调用父组件传入的回调
              if (onCopy) {
                onCopy(address)
              }
            }}
            text={address}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <rect
                x="2.08496"
                y="4.02295"
                width="9.89893"
                height="9.89893"
                rx="2"
                stroke="#F9F9F9"
              />
              <path
                d="M5.85547 2.07861H11.415C12.7957 2.07861 13.915 3.1979 13.915 4.57861V10.0786"
                stroke="#F9F9F9"
                strokeLinecap="round"
              />
            </svg>
          </CopyToClipboard>
        </div>
      )}

      {/* Warning Messages */}
      <div className="border-b border-dashed border-line-divider-secondary pb-4">
        <div className="flex items-start">
          <div className="w-5">
            <img src={MPCDepositWarnIcon} className="w-4 h-4" alt="Warning" />
          </div>
          <div className="flex-1">
            <p className="text-xs text-functional-orange-1">
              {t('deposit.depositAllTip', {
                coin: selectedToken,
                v: currentActiveChain?.chain,
              })}
            </p>
          </div>
        </div>

        {/* Additional Warning for Large Deposits - 只在非 ETH 和非 Sepolia 链显示 */}
        {showLargeDepositWarning && (
          <>
            {/* line */}
            <div className="my-2 ml-5 h-[1px] border-b border-dashed border-line-divider-secondary"></div>
            <div className="flex items-start pl-5 text-functional-orange-1 text-xs">
              {t('deposit.depositTipFor25w')}
            </div>
          </>
        )}
      </div>

      {/* Deposit Information */}
      <div className="text-sm">
        <div className="flex justify-between items-center h-[18px] mt-4">
          <span className="text-text-tertiary">{t('deposit.arrivalTime')}</span>

          <Tooltip
            content={
              <div className="grid flex-grow max-w-[300px] min-w-[80px] rounded-2xl flex-col gap-[10px]">
                <p>
                  {t('deposit.arrivaltimeTooltip1', {
                    v:
                      (currentActiveChain?.blockTime *
                        currentActiveChain.txConfirm) /
                      60,
                    v1: currentActiveChain?.txConfirm,
                  })}
                </p>
                <p>{t('deposit.arrivaltimeTooltip2')}</p>
              </div>
            }
            placement="top"
          >
            <span className="text-text-primary font-medium text-right border-b-[2px] border-dashed border-line-divider-primary decoration-dashed">
              {t('deposit.arrivalEstimated', {
                v:
                  (currentActiveChain?.blockTime *
                    currentActiveChain.txConfirm) /
                  60,
              })}
            </span>
          </Tooltip>
        </div>
        <div className="flex justify-between items-center h-[18px] mt-1">
          <span className="text-text-tertiary">{t('deposit.minDeposit')}</span>
          <span className="text-text-primary font-medium text-right">
            10 {selectedToken}
          </span>
        </div>
        <div className="flex justify-between items-center h-[18px] mt-1">
          <span className="text-text-tertiary">{t('deposit.maxDeposit')}</span>
          <span className="text-text-primary font-medium text-right">
            200,000 {selectedToken}
          </span>
        </div>
      </div>

      {/* 充值按钮 */}
      <button
        className="w-full h-[38px] bg-fill-btn-primary rounded-xl text-sm font-medium text-text-on-sec-btn mt-4"
        onClick={() => {
          if (onDeposit) {
            onDeposit({
              amount: '',
              chainId: currentActiveChain.chainId,
              tokenAddress: address,
              tokenSymbol: selectedToken,
            })
          }
          onClose()
        }}
      >
        {t('deposit.title')}
      </button>
    </>
  )
}

export default MPC
